import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { User } from '../../types/user';
import { ShieldCheck, ShieldX, RefreshCw } from 'lucide-react';
import { Form, FormField, FormLabel, Card } from '../ui';
import { FormInput, FormCpfCnpj, FormPhone, FormEmail } from '../ui/FormControls';
import Button from '../ui/Button';
import Tabs from '../ui/Tabs';

const createAccountSchema = z
  .object({
    username: z
      .string()
      .min(1, 'Usuário é obrigatório')
      .min(6, 'Usuário deve ter no mínimo 6 caracteres'),
    name: z.string().min(1, 'Nome é obrigatório'),
    email: z.string().email('Email inválido'),
    phone: z.string().optional(),
    password: z
      .string()
      .min(1, 'Senha é obrigatória')
      .min(6, 'Senha deve ter no mínimo 6 caracteres')
      .regex(
        /^(?=.*[A-Za-z])(?=.*\d).{6,}$/,
        'Senha deve conter letras, números e caracter especial'
      ),
    confirmPassword: z.string().min(1, 'Confirmação de senha é obrigatória'),
    active: z.boolean().default(true),
    notify_fallbackresponse: z.boolean().default(false),
    notify_queue_limit_timeout: z.boolean().default(false),
    integration_key: z.string().optional(),
    notify_alerts: z.boolean().default(false),
    notify_alerts_resume: z.boolean().default(false),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: 'Senhas não conferem',
    path: ['confirmPassword'],
  });

const updateAccountSchema = z
  .object({
    username: z
      .string()
      .min(1, 'Usuário é obrigatório')
      .min(6, 'Usuário deve ter no mínimo 6 caracteres'),
    name: z.string().min(1, 'Nome é obrigatório'),
    email: z.string().email('Email inválido'),
    phone: z.string().optional(),
    password: z
      .string()
      .optional()
      .refine(
        val => !val || val.length >= 6,
        'Senha deve ter no mínimo 6 caracteres'
      )
      .refine(
        val => !val || /^(?=.*[A-Za-z])(?=.*\d).{6,}$/.test(val),
        'Senha deve conter letras, números e caracter especial'
      ),
    confirmPassword: z.string().optional(),
    active: z.boolean().default(true),
    notify_fallbackresponse: z.boolean().default(false),
    notify_queue_limit_timeout: z.boolean().default(false),
    integration_key: z.string().optional(),
    notify_alerts: z.boolean().default(false),
    notify_alerts_resume: z.boolean().default(false),
  })
  .refine(
    data => {
      if (!data.password && !data.confirmPassword) return true;
      return data.password === data.confirmPassword;
    },
    {
      message: 'Senhas não conferem',
      path: ['confirmPassword'],
    }
  );

type CreateFormData = z.infer<typeof createAccountSchema>;
type UpdateFormData = z.infer<typeof updateAccountSchema>;
type FormData = CreateFormData | UpdateFormData;

interface UserAccountFormProps {
  account: User | null;
  onSubmit: (data: FormData) => void;
  isLoading: boolean;
  onClose: () => void;
  onResetTwoFactor?: (account: User) => void;
  isResettingTwoFactor?: boolean;
}

function UserAccountForm({ account, onSubmit, isLoading, onClose, onResetTwoFactor, isResettingTwoFactor }: UserAccountFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(account ? updateAccountSchema : createAccountSchema),
    defaultValues: {
      username: account?.username || '',
      name: account?.name || '',
      email: account?.email || '',
      phone: account?.phone || '',
      password: '',
      confirmPassword: '',
      active: account?.active ?? true,
      notify_fallbackresponse: account?.notify_fallbackresponse ?? false,
      notify_queue_limit_timeout: account?.notify_queue_limit_timeout ?? false,
      integration_key: account?.integration_key || '',
      notify_alerts: account?.notify_alerts ?? false,
      notify_alerts_resume: account?.notify_alerts_resume ?? false,
    },
  });

  const tabs = [
    {
      id: 'basic',
      label: 'Informações Básicas',
      content: (
        <>
          {!account && (
            <FormField>
              <FormLabel htmlFor="username" required>
                Usuário
              </FormLabel>
              <FormInput
                {...register('username')}
                error={!!errors.username}
                validationError={errors.username?.message}
              />
            </FormField>
          )}

          <FormField>
            <FormLabel htmlFor="name" required>
              Nome
            </FormLabel>
            <FormInput
              {...register('name')}
              error={!!errors.name}
              validationError={errors.name?.message}
            />
          </FormField>

          <FormField>
            <FormLabel htmlFor="email" required>
              Email
            </FormLabel>
            <FormEmail
              {...register('email')}
              error={!!errors.email}
              validationError={errors.email?.message}
            />
          </FormField>

          <FormField>
            <FormLabel htmlFor="phone">Telefone</FormLabel>
            <FormPhone
              {...register('phone')}
              error={!!errors.phone}
              validationError={errors.phone?.message}
            />
          </FormField>

          <FormField>
            <FormLabel htmlFor="password" required={!account}>
              {account ? 'Nova Senha' : 'Senha'}
            </FormLabel>
            <FormInput
              type="password"
              {...register('password')}
              error={!!errors.password}
              validationError={errors.password?.message}
            />
          </FormField>

          <FormField>
            <FormLabel htmlFor="confirmPassword" required={!account}>
              {account ? 'Confirmar Nova Senha' : 'Confirmar Senha'}
            </FormLabel>
            <FormInput
              type="password"
              {...register('confirmPassword')}
              error={!!errors.confirmPassword}
              validationError={errors.confirmPassword?.message}
            />
          </FormField>

          <FormField>
            <div className="flex items-center">
              <input
                type="checkbox"
                {...register('active')}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <FormLabel htmlFor="active" className="ml-2 mb-0">
                Conta ativa
              </FormLabel>
            </div>
          </FormField>
        </>
      ),
    },
    {
      id: 'notifications',
      label: 'Notificações e Integrações',
      content: (
        <>
          <FormField>
            <div className="flex items-center">
              <input
                type="checkbox"
                {...register('notify_fallbackresponse')}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <FormLabel htmlFor="notify_fallbackresponse" className="ml-2 mb-0">
                Notificar resposta de fallback
              </FormLabel>
            </div>
          </FormField>

          <FormField>
            <div className="flex items-center">
              <input
                type="checkbox"
                {...register('notify_queue_limit_timeout')}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <FormLabel htmlFor="notify_queue_limit_timeout" className="ml-2 mb-0">
                Notificar limite de timeout da fila
              </FormLabel>
            </div>
          </FormField>

          <FormField>
            <div className="flex items-center">
              <input
                type="checkbox"
                {...register('notify_alerts')}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <FormLabel htmlFor="notify_alerts" className="ml-2 mb-0">
                Notificar alertas gerais
              </FormLabel>
            </div>
          </FormField>

          <FormField>
            <div className="flex items-center">
              <input
                type="checkbox"
                {...register('notify_alerts_resume')}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <FormLabel htmlFor="notify_alerts_resume" className="ml-2 mb-0">
                Notificar resumo de alertas
              </FormLabel>
            </div>
          </FormField>

          <FormField>
            <FormLabel htmlFor="integration_key">Chave de Integração</FormLabel>
            <FormInput
              {...register('integration_key')}
              error={!!errors.integration_key}
              validationError={errors.integration_key?.message}
              placeholder="Chave para conexão com sistemas externos"
            />
          </FormField>
        </>
      ),
    },
    ...(account && onResetTwoFactor ? [{
      id: 'security',
      label: 'Segurança',
      content: (
        <Card className="p-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary-50 dark:bg-primary-900/20">
              <ShieldCheck className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                Autenticação de Dois Fatores
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Gerencie o 2FA desta conta
              </p>
            </div>
          </div>

          {account.two_factor_enabled ? (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <ShieldCheck className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-green-900 dark:text-green-100">
                    2FA ativado
                  </p>
                  <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                    Esta conta está protegida com autenticação de dois fatores. O usuário precisará informar um código do app autenticador a cada login.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                <RefreshCw className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-amber-900 dark:text-amber-100">
                    Resetar 2FA
                  </p>
                  <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                    Se o usuário perdeu o acesso ao app autenticador, você pode desativar o 2FA para que ele configure novamente no próximo login.
                  </p>
                </div>
              </div>

              <Button
                variant="danger"
                onClick={() => onResetTwoFactor(account)}
                isLoading={isResettingTwoFactor}
              >
                <ShieldX className="h-4 w-4 mr-2" />
                Resetar 2FA do usuário
              </Button>
            </div>
          ) : (
            <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <ShieldX className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  2FA desativado
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Esta conta não possui autenticação de dois fatores ativada. O usuário pode ativar o 2FA nas configurações da própria conta.
                </p>
              </div>
            </div>
          )}
        </Card>
      ),
    }] : []),
  ];

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Tabs tabs={tabs} defaultTab="basic" variant="default" />

      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
        <Button type="submit" isLoading={isLoading} className="sm:col-start-2">
          {account ? 'Salvar' : 'Criar'}
        </Button>
        <Button type="button" variant="secondary" onClick={onClose} className="sm:col-start-1">
          Cancelar
        </Button>
      </div>
    </Form>
  );
}

export default UserAccountForm;
